/**
 * When the query cache must be dropped because the identity behind it changed.
 *
 * Every hook in this package caches under a key built from its own arguments.
 * Most of those keys carry no user id: `['datasets', filters]`,
 * `['episodeFilterOptions']`, `['episodeTags']`. The React Query client
 * outlives a sign-out — the webapp holds one at module scope — so a second
 * sign-in in the same tab reads whatever the previous identity left behind,
 * until each entry goes stale on its own clock.
 *
 * Clearing at the identity boundary fixes that once for every hook. Keying
 * each hook by user id would fix only the hooks that remember to do it, and
 * every hook added later would have to remember again. `useMe` still carries
 * its user id as well, because a shell that renders the wrong organization is
 * the worst version of this bug and one guard should not be the only one.
 *
 * The rule is narrow on purpose: clear only when an identity that was signed
 * in is replaced. A token refresh re-emits the same user id and must not clear,
 * or every refresh would empty a working cache and refetch the whole screen.
 * The first sign-in of a session has nothing before it to leak.
 */
export function shouldClearIdentityCache(
  previousUserId: string | null | undefined,
  nextUserId: string | null | undefined,
): boolean {
  if (!previousUserId) return false;
  return previousUserId !== nextUserId;
}
